import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useApi } from '../../api/api';
import Modal from '../common/Modal/Modal';
import './LogStream.css';

interface LogStreamData {
  service?: string;
  level?: string;
}

const KNOWN_SERVICES = ['bas', 'lek', 'biological', 'economic'] as const;
const serviceNames = {
  bas: 'BAS',
  lek: 'LEK',
  biological: 'Biology',
  economic: 'Economics',
} as const;

interface FormattedLog {
  timestamp?: string;
  service: string;
  message: string;
}

/** Format a raw timestamp value for display */
function formatTimestamp(val: unknown): string | undefined {
  if (val == null) return undefined;
  const s = String(val).trim();
  if (!s) return undefined;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
}

/** Extract service, message, and timestamp from Python dict-like string when JSON.parse fails */
function parsePythonDictLike(str: string): FormattedLog {
  const serviceMatch = str.match(/'service':\s*'([^']*)'/);
  const messageMatch = str.match(/'message':\s*'([^']*)'/);
  const tsMatch =
    str.match(/'timestamp':\s*'([^']*)'/) ??
    str.match(/'time':\s*'([^']*)'/) ??
    str.match(/'created_at':\s*'([^']*)'/);
  return {
    timestamp: tsMatch ? formatTimestamp(tsMatch[1]) : undefined,
    service: serviceMatch ? serviceMatch[1].trim() : '',
    message: messageMatch ? messageMatch[1] : str,
  };
}

function formatLogEntry(log: Record<string, unknown>): FormattedLog {
  const service = String(log.service ?? log.svc ?? '').trim();
  const msg = log.message ?? log.msg ?? JSON.stringify(log);

  if (typeof msg === 'string' && !service && msg.includes("'service':") && msg.includes("'message':")) {
    return parsePythonDictLike(msg);
  }

  const message = typeof msg === 'string' ? msg : JSON.stringify(msg);
  const timestamp = formatTimestamp(
    log.timestamp ?? log.time ?? log.created_at ?? log.ts ?? log.datetime
  );
  return { timestamp, service, message };
}

type TabId = 'log-stream' | 'ai-analysis' | 'diagram';

export default function LogStream(props: RegistryComponentProps): React.ReactElement {
  const data = getRegistryData<LogStreamData>(props);
  const api = useApi() as unknown as {
    streamLogs: (opts: {
      service?: string;
      level?: string;
      fromStart?: boolean;
      onLog?: (log: Record<string, unknown>) => void;
    }) => Promise<{ abort: () => void }>;
    summarizeLogs: (opts: {
      start: string;
      end: string;
      onChunk?: (text: string) => void;
      onError?: (error: string) => void;
      onComplete?: () => void;
    }) => Promise<{ abort: () => void }>;
  };
  const { streamLogs, summarizeLogs } = api;
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('log-stream');
  const [logs, setLogs] = useState<FormattedLog[]>([]);
  const [status, setStatus] = useState<'idle' | 'connecting' | 'streaming' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [aiStatus, setAiStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [aiError, setAiError] = useState<string | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  const aiAbortRef = useRef<(() => void) | null>(null);
  const ignoreAbortRef = useRef(false);
  const logContainerRef = useRef<HTMLPreElement>(null);
  const aiOutputRef = useRef<HTMLDivElement>(null);

  const level = data.level ?? undefined;

  const SERVICE_OPTIONS = [
    { value: '', label: 'All services' },
    { value: 'bas', label: 'Best Available Science' },
    { value: 'lek', label: 'Local Ecological Knowledge' },
    { value: 'biological', label: 'Biology' },
    { value: 'economic', label: 'Economics' },
  ] as const;

  const [selectedService, setSelectedService] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollToBottom = useCallback(() => {
    logContainerRef.current?.scrollTo({
      top: logContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (autoScroll && logs.length > 0) {
      scrollToBottom();
    }
  }, [autoScroll, logs, scrollToBottom]);

  useEffect(() => {
    aiOutputRef.current?.scrollTo({
      top: aiOutputRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [aiSummary]);

  const runAiAnalysis = useCallback(async () => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 30 * 1000);
    const start = startDate.toISOString();
    const end = endDate.toISOString();

    setAiStatus('loading');
    setAiError(null);
    setAiSummary('');

    try {
      const result = await summarizeLogs({
        start,
        end,
        onChunk: (text) => setAiSummary((prev) => prev + text),
        onError: (err) => {
          setAiError(err);
          setAiStatus('error');
        },
        onComplete: () => setAiStatus('idle'),
      });
      aiAbortRef.current = result.abort;
    } catch (err) {
      setAiStatus('error');
      setAiError(err instanceof Error ? err.message : 'Failed to analyze logs');
    }
  }, [summarizeLogs]);

  const startStream = useCallback(async (serviceOverride?: string) => {
    setStatus('connecting');
    setErrorMessage(null);
    setLogs([]);

    const serviceToUse = serviceOverride !== undefined ? serviceOverride : selectedService;

    try {
      const result = await streamLogs({
        service: serviceToUse || undefined,
        level,
        fromStart: true,
        onLog: (log: Record<string, unknown>) => {
          setLogs((prev) => [...prev, formatLogEntry(log)]);
        },
      });
      abortRef.current = result.abort;
      ignoreAbortRef.current = false;
      setStatus('streaming');
    } catch (err) {
      if (ignoreAbortRef.current && err instanceof Error && err.name === 'AbortError') {
        ignoreAbortRef.current = false;
        return;
      }
      ignoreAbortRef.current = false;
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to connect to log stream');
    }
  }, [streamLogs, selectedService, level]);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
    startStream();
  }, [startStream]);

  const handleServiceChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newService = e.target.value;
    setSelectedService(newService);
    if (showModal) {
      ignoreAbortRef.current = true;
      abortRef.current?.();
      abortRef.current = null;
      startStream(newService);
    }
  }, [showModal, startStream]);

  const handleCloseModal = useCallback(() => {
    abortRef.current?.();
    abortRef.current = null;
    aiAbortRef.current?.();
    aiAbortRef.current = null;
    setShowModal(false);
    setLogs([]);
    setStatus('idle');
    setErrorMessage(null);
    setActiveTab('log-stream');
    setAiSummary('');
    setAiStatus('idle');
    setAiError(null);
    document.body.style.overflow = 'auto';
  }, []);

  return (
    <div className="log-stream-component">
      <button
        type="button"
        className="log-stream-button"
        onClick={handleOpenModal}
      >
        View Log Stream
      </button>

      {showModal &&
        createPortal(
          <Modal
            title="Log Stream"
            onClose={handleCloseModal}
            size="extra-large"
            {...({
              customMaxWidth: '90vw',
              customHeight: '90vh',
              customMaxHeight: '90vh',
            } as Record<string, unknown>)}
            bodyStyle={{
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: 1,
              minHeight: 0,
            }}
          >
            <div className="log-stream-modal-content">
              <div className="log-stream-tabs">
                <button
                  type="button"
                  className={`log-stream-tab ${activeTab === 'log-stream' ? 'active' : ''}`}
                  onClick={() => setActiveTab('log-stream')}
                >
                  Log Stream
                </button>
                <button
                  type="button"
                  className={`log-stream-tab ${activeTab === 'ai-analysis' ? 'active' : ''}`}
                  onClick={() => setActiveTab('ai-analysis')}
                >
                  AI Log Analysis
                </button>
                <button
                  type="button"
                  className={`log-stream-tab ${activeTab === 'diagram' ? 'active' : ''}`}
                  onClick={() => setActiveTab('diagram')}
                >
                  Diagram
                </button>
              </div>

              {activeTab === 'log-stream' && (
                <>
                  <div className="log-stream-filters">
                    <label className="log-stream-filter-label">
                      Service:
                      <select
                        value={selectedService}
                        onChange={handleServiceChange}
                        className="log-stream-select"
                        disabled={false}
                      >
                        {SERVICE_OPTIONS.map((opt) => (
                          <option key={opt.value || 'all'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {level && <span>Level: {level}</span>}
                    <label className="log-stream-filter-label log-stream-toggle-label">
                      <input
                        type="checkbox"
                        checked={autoScroll}
                        onChange={(e) => setAutoScroll(e.target.checked)}
                        className="log-stream-checkbox"
                      />
                      Auto-scroll
                    </label>
                  </div>
                  {status === 'connecting' && (
                    <div className="log-stream-status">Connecting to log stream...</div>
                  )}
                  {status === 'error' && (
                    <div className="log-stream-error">{errorMessage}</div>
                  )}
                  <pre ref={logContainerRef} className="log-stream-output">
                    {logs.length === 0 && status === 'streaming' && (
                      <span className="log-stream-waiting">Waiting for logs...</span>
                    )}
                    {logs.map((log, i) => (
                      <div key={i} className="log-stream-line">
                        {log.timestamp && (
                          <span className="log-stream-timestamp">{log.timestamp} </span>
                        )}
                        {log.service ? (
                          <>
                            <span
                              className={
                                KNOWN_SERVICES.includes(
                                  log.service.toLowerCase() as (typeof KNOWN_SERVICES)[number]
                                )
                                  ? `log-stream-service log-stream-service-${log.service.toLowerCase()}`
                                  : 'log-stream-service'
                              }
                            >
                              {serviceNames[log.service as keyof typeof serviceNames] ?? log.service}
                            </span>
                            : {log.message}
                          </>
                        ) : (
                          log.message
                        )}
                      </div>
                    ))}
                  </pre>
                </>
              )}

              {activeTab === 'ai-analysis' && (
                <>
                  <div className="log-stream-ai-actions">
                    <button
                      type="button"
                      className="log-stream-button"
                      onClick={runAiAnalysis}
                      disabled={aiStatus === 'loading'}
                    >
                      {aiStatus === 'loading' ? 'Analyzing...' : 'Analyze last 30 seconds'}
                    </button>
                  </div>
                  {aiStatus === 'error' && aiError && (
                    <div className="log-stream-error">{aiError}</div>
                  )}
                  <div ref={aiOutputRef} className="log-stream-output log-stream-ai-output log-stream-markdown">
                    {aiSummary ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiSummary}</ReactMarkdown>
                    ) : aiStatus === 'idle' && !aiError ? (
                      <span className="log-stream-waiting">
                        Click &quot;Analyze last 30 seconds&quot; to generate an AI summary of recent logs.
                      </span>
                    ) : null}
                  </div>
                </>
              )}

              {activeTab === 'diagram' && (
                <div className="log-stream-diagram-container">
                  <img
                    src="/images/event-streaming-diagram.png"
                    alt="Event streaming architecture diagram"
                    className="log-stream-diagram-img"
                  />
                </div>
              )}
            </div>
          </Modal>,
          document.body
        )}
    </div>
  );
}
